import type { ComponentPropsWithRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn.ts';

/*
 * A note pinned to a label: what an answer has to satisfy ("kies er minstens
 * 1"), or what Wundo would advise ("aanbevolen").
 *
 * It never says what the thing beside it *is* — TypePill classifies a
 * conversation, SourceBadge says where a line came from, VisibilityBadge says
 * who can see a surface. This one only qualifies the words next to it, which is
 * why it carries no icon.
 *
 * `quiet` and `accent` are that original pair. The five below them are the
 * status vocabulary a table needs — a row's state said in one word — and they
 * are the same shape on purpose: a status pill is still a note about the row
 * beside it, not a second kind of object.
 *
 * On `warning`: this theme has no amber. The brand pink and its `on-pink` ink
 * are the only warm pair that is not a gesprekstype colour (borrowing one of
 * those would make a status read as a conversation type), and they clear the
 * contrast floor at 7:1, so warning is pink here rather than yellow.
 */
const badgeVariants = cva(
  'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-2xs font-bold tracking-wide whitespace-nowrap',
  {
    variants: {
      tone: {
        quiet: 'bg-fill text-ink-muted',
        accent: 'bg-accent-tint text-success',
        /* A state with nothing to say about it — "draft", "web". */
        neutral: 'bg-fill text-ink-secondary',
        /* A fact worth reading, in the brand's own navy rather than a stock blue. */
        info: 'bg-fill text-ink ring-1 ring-line',
        success: 'bg-accent-tint text-success',
        warning: 'bg-pink-tint text-on-pink',
        danger: 'bg-danger-fill text-danger',
        /* On the navy hero bar, where every light tone above disappears. */
        onPrimary: 'bg-on-primary-fill text-white ring-1 ring-on-primary-line',
      },
    },
    defaultVariants: {
      tone: 'quiet',
    },
  },
);

type BadgeVariants = VariantProps<typeof badgeVariants>;

export type BadgeTone = NonNullable<BadgeVariants['tone']>;

export interface BadgeProps
  extends Omit<ComponentPropsWithRef<'span'>, 'style'>, BadgeVariants {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return (
    <span
      data-slot='badge'
      data-tone={tone ?? 'quiet'}
      className={cn(badgeVariants({ tone }), className)}
      {...props}
    />
  );
}

export { badgeVariants };
